import { useQuery } from '@tanstack/react-query'
import { ArrowRight, CheckCircle2, Clock, Database, PlusCircle } from 'lucide-react'
import { Link } from 'react-router-dom'
import { jobsApi, getApiErrorMessage } from '@/services/api'
import { formatDate, formatNumber, clampPercent } from '@/lib/format'

const statusConfig: Record<string, { label: string; color: string }> = {
  pending: { label: 'Ожидает', color: '#f59e0b' },
  running: { label: 'Запущен', color: '#3b82f6' },
  paused: { label: 'Пауза', color: '#94a3b8' },
  completed: { label: 'Завершён', color: '#10b981' },
  failed: { label: 'Ошибка', color: '#ef4444' },
  cancelled: { label: 'Отменён', color: '#64748b' },
  waiting_user: { label: 'Ожидает юзера', color: '#f97316' },
}

export default function Dashboard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['jobs', 'dashboard'],
    queryFn: () => jobsApi.list(1, 20),
    refetchInterval: 10000,
  })

  const jobs = data?.items ?? []
  const activeJobs = jobs.filter(j => ['running', 'paused', 'waiting_user'].includes(j.status)).length
  const completedJobs = jobs.filter(j => j.status === 'completed').length
  const totalCompanies = jobs.reduce((s, j) => s + j.progress.scraped_companies, 0)

  return (
    <div className="fade-in" style={{ maxWidth: 1200 }}>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2>Панель управления</h2>
          <p style={{ color: 'var(--text-muted)', marginTop: 6 }}>Мониторинг задач и прогресс сбора данных</p>
        </div>
        <Link to="/jobs/new" className="btn btn-primary">
          <PlusCircle size={16} /> Новый сбор
        </Link>
      </div>

      {error && <div style={{ padding: 16, background: 'rgba(239,68,68,0.1)', borderRadius: 'var(--radius-md)', color: 'var(--danger)', marginBottom: 16 }}>{getApiErrorMessage(error)}</div>}

      <div className="metrics-grid" style={{ marginBottom: 32 }}>
        <div className="metric-card fade-in stagger-1">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-md)', background: 'rgba(99,102,241,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Database size={20} style={{ color: 'var(--accent)' }} />
            </div>
            <div className="metric-label">Всего задач</div>
          </div>
          <div className="metric-value">{formatNumber(data?.total ?? 0)}</div>
        </div>
        <div className="metric-card fade-in stagger-2">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-md)', background: 'rgba(59,130,246,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Clock size={20} style={{ color: '#60a5fa' }} />
            </div>
            <div className="metric-label">Активны</div>
          </div>
          <div className="metric-value" style={{ color: '#60a5fa' }}>{activeJobs}</div>
        </div>
        <div className="metric-card fade-in stagger-3">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-md)', background: 'rgba(16,185,129,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle2 size={20} style={{ color: '#34d399' }} />
            </div>
            <div className="metric-label">Завершены</div>
          </div>
          <div className="metric-value" style={{ color: '#34d399' }}>{completedJobs}</div>
        </div>
        <div className="metric-card fade-in stagger-4">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-md)', background: 'rgba(168,85,247,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Database size={20} style={{ color: '#a855f7' }} />
            </div>
            <div className="metric-label">Компаний собрано</div>
          </div>
          <div className="metric-value" style={{ color: '#a855f7' }}>{formatNumber(totalCompanies)}</div>
        </div>
      </div>

      <div className="section-header">
        <div>
          <div className="section-title">Задачи сбора</div>
          <div className="section-desc">Управление сценариями сбора данных с checko.ru</div>
        </div>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', gap: 16, flexDirection: 'column' }}>
          {[1, 2, 3].map(i => <div key={i} className="loading-skeleton" style={{ height: 80 }} />)}
        </div>
      ) : jobs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><Database size={36} /></div>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Нет задач</h3>
          <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>Создайте первую задачу сбора данных</p>
          <Link to="/jobs/new" className="btn btn-primary"><PlusCircle size={16} /> Создать задачу</Link>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {jobs.map(job => {
            const cfg = statusConfig[job.status] || statusConfig.pending
            const pct = clampPercent(job.progress.progress_percent)
            return (
              <Link key={job.id} to={`/jobs/${job.id}`} className="job-card fade-in">
                <div className="job-card-icon" style={{ background: `${cfg.color}18`, color: cfg.color }}>
                  <Database size={22} />
                </div>
                <div className="job-card-info">
                  <h3>{job.name || `Задача #${job.id}`}</h3>
                  <p>{formatNumber(job.progress.scraped_companies)} компаний · {pct.toFixed(0)}%</p>
                </div>
                <div style={{ minWidth: 120 }}>
                  <div className="progress-bar" style={{ marginBottom: 6 }}>
                    <div className="progress-bar-fill" style={{ width: `${pct}%`, background: cfg.color === '#3b82f6' ? undefined : cfg.color }} />
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'right' }}>{formatDate(job.created_at)}</div>
                </div>
                <span className={`badge badge--${job.status}`}>{cfg.label}</span>
                <ArrowRight size={16} style={{ color: 'var(--text-muted)' }} />
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
